import {
  NUMBERED_REF_TOKEN_RE,
  collapseAdjacentDuplicateRefTokens,
} from '../citationTokens';

/** Presentation only. Call after source-admission and numbering checks: the
 * original reliability tokens remain evidence metadata in the saved report.
 * Keep citations, unverified warnings, and free-text qualifications visible. */
export function presentReportReliabilityTokens(text: string): string {
  const expanded = text
    .replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), token => {
      const hasSoftReliability = /\b(?:verified|inferred)\s+/i.test(token);
      if (!hasSoftReliability) {
        const nums = [...token.matchAll(/\d+/g)].map(m => m[0]);
        if (new Set(nums).size === nums.length) return token;
      }
      const inherited = /^\[(verified|inferred|unverified)\s+/i.exec(token)?.[1]?.toLowerCase();
      // The ordinary citation normalizer cannot expand a mixed group such as
      // [Ref 1, unverified Ref 2]. Emit independently renderable tokens while
      // preserving each index and any explicit or inherited warning.
      return token.slice(1, -1).split(/\s*,\s*/).map(member => {
        const match = /^(?:(verified|inferred|unverified)\s+)?(?:Ref\s*(?::\s*)?)?(\d+)$/i.exec(member.trim())!;
        const reliability = match[1]?.toLowerCase() ?? inherited;
        return `[${reliability === 'unverified' ? 'unverified ' : ''}Ref ${match[2]}]`;
      }).join(', ');
    })
    .replace(/\[(?:verified|inferred)(?:\s+((?!Ref\b)[^\]]+))?\]/gi, (_token, qualifier: string | undefined) =>
      qualifier?.trim() ? `(${qualifier.trim()})` : '');
  const presented = collapseAdjacentDuplicateRefTokens(expanded)
    .replace(/\(\s*[,;:]\s*/g, '(')
    .replace(/\s*[,;:]\s*\)/g, ')')
    .replace(/(?<![A-Za-z0-9_$])\s*\(\s*\)/g, '');
  if (presented === text) return text;
  return presented.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([.,;:!?])/g, '$1');
}
